import React, { useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface AtmosphereScatterProps {
  width?: number;
  height?: number;
}

export const AtmosphereScatter: React.FC<AtmosphereScatterProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const particlesRef = useRef(Array.from({ length: 40 }).map(() => ({
    x: Math.random() * size,
    y: Math.random() * -size, // Start above
    speed: 2 + Math.random() * 2,
    type: Math.random() > 0.5 ? 'blue' : 'red',
    scatterAngle: 0,
    isScattered: false
  })));

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A'; // Space
    ctx.fillRect(0, 0, size, size);

    const atmoTop = size * 0.4;
    const ground = size * 0.9;

    // Draw Sky Gradient
    const skyGrad = ctx.createLinearGradient(0, atmoTop, 0, ground);
    skyGrad.addColorStop(0, '#1E3A8A'); // Dark blue
    skyGrad.addColorStop(1, '#EA580C'); // Sunset orange
    
    ctx.beginPath();
    ctx.moveTo(0, ground);
    ctx.quadraticCurveTo(size / 2, atmoTop - 20, size, ground);
    ctx.lineTo(size, size);
    ctx.lineTo(0, size);
    ctx.fillStyle = skyGrad;
    ctx.fill();

    // Draw Sun
    ctx.save();
    const sunGrad = ctx.createRadialGradient(size/2, 40, 5, size/2, 40, 30);
    sunGrad.addColorStop(0, '#FFFFFF');
    sunGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(size/2, 40, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw Observer (stick figure)
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    const obsX = size / 2;
    const obsY = ground + 20;
    ctx.beginPath();
    ctx.arc(obsX, obsY - 15, 5, 0, Math.PI * 2); // Head
    ctx.moveTo(obsX, obsY - 10);
    ctx.lineTo(obsX, obsY + 5); // Body
    ctx.moveTo(obsX - 10, obsY - 5);
    ctx.lineTo(obsX + 10, obsY - 5); // Arms
    ctx.moveTo(obsX, obsY + 5);
    ctx.lineTo(obsX - 8, obsY + 15); // Leg L
    ctx.moveTo(obsX, obsY + 5);
    ctx.lineTo(obsX + 8, obsY + 15); // Leg R
    ctx.stroke();

    // Update & Draw Particles
    particlesRef.current.forEach(p => {
      // Move down
      if (!p.isScattered) {
        p.y += p.speed;
        
        // Check if hit atmosphere
        if (p.y > atmoTop && p.type === 'blue') {
          p.isScattered = true;
          // Random scatter angle (not straight down)
          p.scatterAngle = (Math.random() * Math.PI) + Math.PI; 
        }
      } else {
        // Scattered blue particle movement
        p.x += Math.cos(p.scatterAngle) * p.speed;
        p.y += Math.sin(p.scatterAngle) * p.speed;
      }

      // Reset if off screen
      if (p.y > size + 20 || p.x < -20 || p.x > size + 20) {
        p.y = -20;
        p.x = 20 + Math.random() * (size - 40);
        p.isScattered = false;
      }

      // Draw
      ctx.beginPath();
      if (p.type === 'blue') {
        ctx.strokeStyle = '#60A5FA';
        ctx.lineWidth = 2;
        ctx.moveTo(p.x, p.y);
        if (p.isScattered) {
          ctx.lineTo(p.x - Math.cos(p.scatterAngle)*10, p.y - Math.sin(p.scatterAngle)*10);
        } else {
          ctx.lineTo(p.x, p.y - 10);
        }
      } else {
        // Red/Yellow (passes straight through)
        ctx.strokeStyle = '#FDE047';
        ctx.lineWidth = 2.5;
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x, p.y - 15);
      }
      ctx.stroke();
    });

    // Labels
    ctx.fillStyle = '#60A5FA';
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Blue light scatters → Sky looks blue', 10, atmoTop + 40);

    ctx.fillStyle = '#FDE047';
    ctx.textAlign = 'right';
    ctx.fillText('Red/yellow reaches eyes → Sun looks yellow', size - 10, ground - 20);

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
